import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  useGetUserById,
  useProfileImageDelete,
  useProfileImageUpload,
  usePutUpdateUserData
} from "../../../../api/userDetails";
import { getUserRolesInLocal } from "../../../../utils/cacheStorage";
import { successMessage } from "../../../../helpers/ErrorHandler";
import { buildUpdateUserPayload } from "../../shared/services/userPayload";
import { createEditRolePolicy } from "../../shared/services/userRolePolicy";
import { UserFormData } from "../../shared/types/user.types";
import { UserEditModalProps, UserEditViewModel } from "../types/userEditView.types";

/**
 * Ports the state/submit logic of the legacy `UserEditModal`
 * (src/screens/user/userEdit/UserEditModal.tsx): the user is loaded through
 * the same `useGetUserById` mutation each time the drawer opens, PUT carries
 * the same payload (`status: ACTIVE`, one-item role array), and the profile
 * picture is uploaded / deleted immediately, then the user is reloaded to
 * refresh the preview. The role is read-only once registered, except for administrators.
 */
export const useUserEditViewModel = ({
  visibleModal,
  id,
  closeModal,
  onSuccess
}: Omit<UserEditModalProps, "roles">): UserEditViewModel => {
  const rolePolicy = useMemo(() => createEditRolePolicy(getUserRolesInLocal()?.[0]), []);

  const {
    data: userDetails,
    isLoading: isLoadingDetails,
    mutate: loadUser,
    reset: resetUser
  } = useGetUserById();
  const { isLoading: isUpdating, mutate: updateUser, reset: resetUpdate } = usePutUpdateUserData();
  const { isLoading: isUploading, mutate: uploadProfileImage } = useProfileImageUpload();
  const { isLoading: isDeletingImage, mutate: deleteProfileImage } = useProfileImageDelete();

  const values = useMemo<UserFormData>(
    () => ({
      firstName: userDetails?.firstName || "",
      lastName: userDetails?.lastName || "",
      email: userDetails?.email || "",
      phone: userDetails?.phone || "",
      role: userDetails?.userRole?.[0] || ""
    }),
    [userDetails]
  );

  const methods = useForm<UserFormData>({ mode: "all", defaultValues: values, values });
  const { handleSubmit, reset } = methods;

  useEffect(() => {
    if (visibleModal && id) {
      loadUser(id);
    }
    return () => {
      reset();
      resetUser();
      resetUpdate();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleModal]);

  const onSubmit = (data: UserFormData) => {
    if (!userDetails?._id) return;
    updateUser(
      { id: userDetails._id, userData: buildUpdateUserPayload(data) },
      {
        onSuccess: () => {
          successMessage("User data update");
          closeModal();
          onSuccess();
        }
      }
    );
  };

  const onAvatarUpload = (file: File) => {
    if (!userDetails?._id) return;
    uploadProfileImage({ userId: userDetails._id, file }, { onSuccess: () => loadUser(userDetails._id) });
  };

  const onAvatarRemove = () => {
    if (!userDetails?._id) return;
    deleteProfileImage({ userId: userDetails._id }, { onSuccess: () => loadUser(userDetails._id) });
  };

  const onCancel = () => {
    closeModal();
    reset();
  };

  return {
    methods,
    rolePolicy,
    isLoadingDetails,
    profileUrl: userDetails?.profileUrl ?? null,
    initial: (userDetails?.firstName?.[0] || "U").toUpperCase(),
    isAvatarBusy: isUploading || isDeletingImage,
    onAvatarUpload,
    onAvatarRemove,
    isSaving: isUpdating,
    onSave: handleSubmit(onSubmit),
    onCancel
  };
};
